import React, { useMemo } from 'react'
import { Route } from 'react-router-dom'
import { useMenu } from '../hooks/useMenu'
import { useAuth } from '../contexts/AuthContext'
import DynamicTelaPage from '../pages/DynamicTelaPage'
import { TelaResponse } from '../types/api'

/**
 * Hook que retorna rotas dinâmicas baseadas nas telas do backend
 * 
 * Busca telas do tipo GALATEA_WEB e cria rotas para cada uma.
 * As rotas são protegidas automaticamente pelo ProtectedRoute do Layout.
 * 
 * Retorna um array de elementos Route que podem ser usados dentro de Routes
 */
export const useDynamicRoutes = () => {
  const { isAuthenticated, user } = useAuth()
  const { menuItems, isLoading } = useMenu('GALATEA_WEB')

  const routes = useMemo(() => {
    // Só criar rotas se o usuário estiver autenticado
    if (!isAuthenticated || !user) {
      return []
    }
    
    if (isLoading || !menuItems || menuItems.length === 0) {
      return []
    }

    // Função recursiva para criar rotas de telas e seus filhos
    const createRoutes = (telas: (TelaResponse & { children?: TelaResponse[] })[]): React.ReactElement[] => {
      const routeElements: React.ReactElement[] = []

      const rotasBasicas = ['/basicas/', '/funcionais/', '/reservadas/']
      const isRotaBasica = (r: string) => rotasBasicas.some((p) => r.startsWith(p))

      telas.forEach((tela) => {
        // Criar rota para a tela atual (exceto basicas/funcionais/reservadas — usam BasicaPage)
        if (tela.ativo && !tela.deleted_at && tela.rota && !isRotaBasica(tela.rota)) {
          let rotaPath = tela.rota.startsWith('/') ? tela.rota.slice(1) : tela.rota

          routeElements.push(
            <Route
              key={`tela-${tela.id}`}
              path={rotaPath}
              element={<DynamicTelaPage />}
            />
          )
        }

        // Criar rotas para filhos recursivamente
        if (tela.children && tela.children.length > 0) {
          routeElements.push(...createRoutes(tela.children))
        }
      })

      return routeElements
    }

    return createRoutes(menuItems)
  }, [menuItems, isLoading, isAuthenticated, user])

  return routes
}

/**
 * Componente que renderiza rotas dinâmicas
 * @deprecated Use useDynamicRoutes hook diretamente no App.tsx
 */
const DynamicRoutes: React.FC = () => {
  const routes = useDynamicRoutes()
  return <>{routes}</>
}

export default DynamicRoutes

